import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Última actualización: Febrero 2026

1. INFORMACIÓN QUE RECOPILAMOS
Recopilamos únicamente la información necesaria para procesar su compra y entregar el producto digital:
• Nombre completo
• Dirección de correo electrónico
• Información de pago (procesada de forma segura por terceros)

2. USO DE LA INFORMACIÓN
Su información personal se utiliza exclusivamente para:
• Procesar y confirmar su compra
• Enviar el producto digital adquirido
• Comunicar actualizaciones importantes sobre el producto

3. PROTECCIÓN DE DATOS
Implementamos medidas de seguridad estándar de la industria para proteger su información personal. No almacenamos datos de tarjetas de crédito en nuestros servidores.

4. COMPARTIR INFORMACIÓN
No vendemos, intercambiamos ni transferimos su información personal a terceros. La única excepción son los proveedores de servicios de pago necesarios para procesar su transacción.

5. COOKIES
Nuestro sitio puede utilizar cookies para mejorar su experiencia de navegación. Puede configurar su navegador para rechazar cookies.

6. SUS DERECHOS
Usted tiene derecho a:
• Acceder a sus datos personales
• Solicitar la corrección de datos inexactos
• Solicitar la eliminación de sus datos
• Retirar su consentimiento en cualquier momento

7. CAMBIOS A ESTA POLÍTICA
Nos reservamos el derecho de actualizar esta política de privacidad. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
Última actualización: Febrero 2026

1. ACEPTACIÓN DE TÉRMINOS
Al adquirir la "Guía Informativa — Edición 2026", usted acepta estos términos y condiciones en su totalidad.

2. DESCRIPCIÓN DEL PRODUCTO
Este es un producto digital educativo en formato PDF. El contenido es de carácter informativo y no constituye asesoramiento legal, financiero ni fiscal personalizado.

3. ENTREGA DEL PRODUCTO
Tras completar el pago, recibirá acceso inmediato al producto digital a través del correo electrónico proporcionado durante la compra.

4. POLÍTICA DE REEMBOLSO
Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si experimenta problemas técnicos para acceder al producto, contáctenos y le asistiremos.

5. USO PERMITIDO
El producto es para uso personal únicamente. Queda prohibido:
• Reproducir, distribuir o revender el contenido
• Compartir el archivo con terceros
• Utilizar el contenido con fines comerciales sin autorización

6. PROPIEDAD INTELECTUAL
Todo el contenido de la guía, incluyendo textos, gráficos y diseño, está protegido por derechos de autor. Todos los derechos están reservados.

7. LIMITACIÓN DE RESPONSABILIDAD
La información proporcionada es de carácter general y educativo. No nos hacemos responsables de decisiones financieras tomadas basándose en este material. Recomendamos consultar con un profesional certificado para situaciones específicas.

8. DESCARGO
La información refleja las normas vigentes al momento de la publicación. No estamos afiliados a ninguna agencia gubernamental de los Estados Unidos.

9. NORMAS APLICABLES
Estos términos se rigen por las normas de los Estados Unidos de América.

10. MODIFICACIONES
Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación.
`.trim();
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label={title} data-id="element-44">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-45" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-46">
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gray-100 bg-[#1B2A4A] shrink-0" data-id="element-47">
          <h2 className="text-xl md:text-2xl font-bold text-[#C5A55A] font-serif" data-id="element-48">
            {title}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Cerrar" data-id="element-49">
            <X className="w-6 h-6" data-id="element-50" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 md:px-8 py-6 text-[#2D2D2D] text-base leading-relaxed whitespace-pre-line" data-id="element-51">
          {content}
        </div>

        {/* Footer */}
        <div className="px-6 md:px-8 py-4 border-t border-gray-100 shrink-0" data-id="element-52">
          <button onClick={onClose} className="w-full bg-[#1B2A4A] hover:bg-[#0F1D33] text-white font-semibold py-3 px-6 rounded-full transition-colors text-base" data-id="element-53">
            Entendido
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#0A1628] py-10 px-4 sm:px-6 lg:px-8" data-id="element-54">
        <div className="max-w-4xl mx-auto text-center space-y-4" data-id="element-55">
          <div className="flex items-center justify-center gap-6" data-id="element-56">
            <button onClick={() => setActiveModal('privacy')} className="text-[#E8D5A3]/70 hover:text-[#C5A55A] text-sm underline underline-offset-4 transition-colors" data-id="element-57">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-58">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-[#E8D5A3]/70 hover:text-[#C5A55A] text-sm underline underline-offset-4 transition-colors" data-id="element-59">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-white/30 text-sm" data-id="element-60">
            © {new Date().getFullYear()} Guía Informativa. Todos los derechos
            reservados.
          </p>
          <p className="text-white/20 text-xs max-w-3xl mx-auto" data-id="element-61">
            Descargo de responsabilidad: Este material tiene fines educativos e
            informativos únicamente y no constituye asesoramiento legal,
            financiero o fiscal. No estamos afiliados a ninguna agencia
            gubernamental.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-62">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-63" />}
      </AnimatePresence>
    </>;
}